import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Loader2 } from 'lucide-react'

export function ProvisionPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    id: '',
    name: '',
    operator: '',
    operator_short: '',
    domain: '',
    primary_color: '#5A78FF',
    mssql_database: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const canSubmit = form.id && form.name && form.operator && form.domain

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return

    setSaving(true)
    setError('')

    try {
      const res = await fetch('/api/portal/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) {
        navigate(`/portal/tenants/${form.id}`)
      } else {
        const data = await res.json().catch(() => null)
        setError(data?.message || 'Failed to create tenant')
      }
    } catch {
      setError('Network error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/portal" className="text-ink-soft hover:text-ink-dark transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-ink-dark">New Copilot</h1>
          <p className="text-sm text-ink-soft">Create a new client copilot deployment.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 p-6 space-y-5">
        <Field label="Tenant ID" hint="Lowercase, no spaces (e.g. 'talley')">
          <input
            type="text"
            value={form.id}
            onChange={e => update('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className="input-field"
            placeholder="talley"
          />
        </Field>

        <Field label="Copilot Name" hint="Display name (e.g. 'Talley Tools')">
          <input
            type="text"
            value={form.name}
            onChange={e => update('name', e.target.value)}
            className="input-field"
            placeholder="Talley Tools"
          />
        </Field>

        <Field label="Operator" hint="Full company name">
          <input
            type="text"
            value={form.operator}
            onChange={e => update('operator', e.target.value)}
            className="input-field"
            placeholder="Talley Amusements"
          />
        </Field>

        <Field label="Operator Short" hint="Abbreviated name for headers">
          <input
            type="text"
            value={form.operator_short}
            onChange={e => update('operator_short', e.target.value)}
            className="input-field"
            placeholder="Talley"
          />
        </Field>

        <Field label="Domain" hint="Where this copilot will be deployed">
          <input
            type="text"
            value={form.domain}
            onChange={e => update('domain', e.target.value)}
            className="input-field"
            placeholder="talley.magicmeade.cloud"
          />
        </Field>

        <Field label="Brand Color" hint="Primary brand color">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.primary_color}
              onChange={e => update('primary_color', e.target.value)}
              className="w-10 h-10 rounded-lg border border-slate-200 cursor-pointer"
            />
            <input
              type="text"
              value={form.primary_color}
              onChange={e => update('primary_color', e.target.value)}
              className="input-field flex-1"
            />
          </div>
        </Field>

        <Field label="MSSQL Database" hint="Production database name (optional — configure later)">
          <input
            type="text"
            value={form.mssql_database}
            onChange={e => update('mssql_database', e.target.value)}
            className="input-field"
            placeholder="MagicMoney_Talley_Production"
          />
        </Field>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={!canSubmit || saving}
            className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create Copilot
          </button>
          <Link
            to="/portal"
            className="text-sm font-medium text-ink-soft hover:text-ink-dark"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-dark mb-1">{label}</label>
      <p className="text-xs text-ink-soft mb-2">{hint}</p>
      {children}
    </div>
  )
}
